import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  lockedR32SlotTeamIds,
  resolveBracket,
  type MatchPublic,
  type ResolvedTeams,
  type TeamPublic,
} from '@mondy/shared';
import { api } from '@/api';
import { buildGroups } from '@/components/GroupStandings';

interface TeamsResponse {
  teams: TeamPublic[];
}
interface MatchListResponse {
  matches: MatchPublic[];
}

export interface BracketProjection {
  /** matchId → projected {team1, team2} for knockout matches (null until loaded). */
  resolved: Map<string, ResolvedTeams> | null;
  /** Team ids whose projected R32 slot is 100% locked; null once the group stage is over. */
  lockedIds: Set<string> | null;
}

const EMPTY: BracketProjection = { resolved: null, lockedIds: null };

// ponytail: memoize-one keyed on the (teams, matches) array refs. TanStack hands
// back stable refs between the 30s refetches, so every MatchCard instance shares
// ONE run of the non-trivial groups/projection/locks compute. Fine in a no-SSR SPA.
let memo: { teams: TeamPublic[]; matches: MatchPublic[]; value: BracketProjection } | null = null;
function project(teams: TeamPublic[], matches: MatchPublic[]): BracketProjection {
  if (memo && memo.teams === teams && memo.matches === matches) return memo.value;
  const groups = buildGroups(teams, matches);
  const pending = matches.some((m) => m.stage === 'group' && m.status !== 'finished');
  const value: BracketProjection = {
    resolved: resolveBracket(matches, groups),
    lockedIds: pending ? lockedR32SlotTeamIds(groups, matches) : null,
  };
  memo = { teams, matches, value };
  return value;
}

/**
 * Projected knockout-bracket teams + their position-certainty, for surfaces that
 * show a single match (cards, match detail). Shares the Teams page's exact query
 * cache (`['teams']` + `['matches','all']`) so a projected team and its ✓/? are
 * identical to the bracket for the same match — by construction. `enabled` keeps
 * group-only pages from pulling the all-matches list for nothing.
 */
export function useBracketProjection(enabled: boolean): BracketProjection {
  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
    enabled,
  });
  const matchesQuery = useQuery({
    queryKey: ['matches', 'all'],
    queryFn: () => api.get<MatchListResponse>('/api/matches'),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
    enabled,
  });
  return useMemo(() => {
    if (!enabled || !teamsQuery.data || !matchesQuery.data) return EMPTY;
    return project(teamsQuery.data.teams, matchesQuery.data.matches);
  }, [enabled, teamsQuery.data, matchesQuery.data]);
}
